import React, { useEffect } from 'react';
import confetti from 'canvas-confetti';
import { Sparkles, Trophy, Play, ArrowRight, Flame } from 'lucide-react';
import { PhraseItem } from '../types/game';

interface WinModalProps {
  phraseItem: PhraseItem;
  replaysCount: number;
  hintsUsed: number;
  streak: number;
  onPlayForward: () => void;
  onNextPhrase: () => void;
}

export const WinModal: React.FC<WinModalProps> = ({
  phraseItem,
  replaysCount,
  hintsUsed,
  streak,
  onPlayForward,
  onNextPhrase,
}) => {
  useEffect(() => {
    confetti({
      particleCount: 80,
      spread: 70,
      origin: { y: 0.6 },
      colors: ['#6366f1', '#ec4899', '#10b981', '#f59e0b', '#06b6d4'],
    });
  }, []);

  return (
    <div className="w-full max-w-xl bg-gradient-to-b from-indigo-950/90 via-slate-900 to-slate-900 border-2 border-indigo-500/50 rounded-2xl p-6 shadow-2xl shadow-indigo-500/20 my-4 flex flex-col items-center gap-4 text-center animate-pop-in">
      <div className="w-12 h-12 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center text-emerald-400">
        <Trophy className="w-6 h-6 animate-bounce-short" />
      </div>

      <div>
        <div className="flex items-center justify-center gap-1.5 text-xs font-extrabold uppercase tracking-widest text-emerald-400">
          <Sparkles className="w-4 h-4" />
          <span>Correctly Unlocked!</span>
        </div>
        <h2 className="text-2xl sm:text-3xl font-black text-white mt-1 tracking-tight">
          "{phraseItem.phrase}"
        </h2>
      </div>

      {/* Round Performance Breakdown */}
      <div className="flex items-center justify-center gap-4 py-2.5 px-4 bg-slate-950/60 rounded-xl border border-slate-800 text-xs text-slate-300 w-full max-w-sm">
        <div>
          <span className="text-slate-400 block">Replays</span>
          <span className="font-bold text-indigo-300 text-sm">{replaysCount}</span>
        </div>
        <div className="h-6 w-px bg-slate-800" />
        <div>
          <span className="text-slate-400 block">Hints Used</span>
          <span className="font-bold text-amber-300 text-sm">{hintsUsed}</span>
        </div>
        <div className="h-6 w-px bg-slate-800" />
        <div>
          <span className="text-slate-400 block">Streak</span>
          <span className="font-bold text-emerald-400 text-sm flex items-center justify-center gap-0.5">
            <Flame className="w-3.5 h-3.5 fill-current" />
            {streak}
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap items-center justify-center gap-3 w-full pt-1">
        <button
          onClick={onPlayForward}
          className="py-3 px-5 rounded-xl font-bold text-sm bg-slate-800 hover:bg-slate-700 text-emerald-300 border border-emerald-500/40 flex items-center gap-2 transition-all transform hover:-translate-y-0.5"
        >
          <Play className="w-4 h-4 fill-current" />
          <span>Listen Original</span>
        </button>

        <button
          onClick={onNextPhrase}
          className="py-3 px-6 rounded-xl font-bold text-sm bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white shadow-lg shadow-indigo-500/30 flex items-center gap-2 transition-all transform hover:-translate-y-0.5 active:scale-95"
        >
          <span>Next Word/Phrase</span>
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
